import * as THREE from 'three/webgpu'
import { shapeCircle } from 'three/tsl'
import type { GlbDatumOriginFrame } from './resolve-frame'

/**
 * 原点 Sprite 的世界单位尺寸。
 *
 * 说明：
 * 1. 与项目内 [DatumPoint] / [ReferencePointPreviewRenderer] 的约定保持一致（0.008）；
 * 2. 在 `sizeAttenuation: false` 的 `SpriteNodeMaterial` 下，这已足够稳定地渲染为视口里的小圆点；
 * 3. 这里不再使用 `frame.sizePx`，info-only datum-slot 不需要按宿主大小动态缩放。
 */
const ORIGIN_SPRITE_WORLD_SCALE = 0.008

/**
 * 创建 datum-slot 原点点标记对象。
 *
 * 说明：
 * 1. 沿用项目 [PointsMaterialFactory] 约定——点标记走 `Sprite + SpriteNodeMaterial`，
 *    用 TSL `shapeCircle()` 做 opacityNode 生成圆形遮罩；
 * 2. 不使用 `THREE.Points + PointsMaterial` 路径，后者在 WebGPU renderer 下显示异常。
 *
 * @param color 点标记颜色。
 * @returns 原点 Sprite 对象。
 */
export function createOriginPoint(color: number): THREE.Sprite {
  const material = new THREE.SpriteNodeMaterial({
    color,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    sizeAttenuation: false,
    alphaToCoverage: true,
  })
  material.opacityNode = shapeCircle()

  const sprite = new THREE.Sprite(material)
  sprite.name = 'glb-datum-slot-origin-point'
  sprite.frustumCulled = false
  sprite.userData.isGlbDatumSlotOverlay = true
  sprite.raycast = () => undefined
  sprite.renderOrder = 6
  return sprite
}

/**
 * 将 datum-slot 原点 frame 更新到 Sprite。
 *
 * @param frame 原点 frame。
 * @param color 点标记颜色。
 * @param sprite 复用中的 Sprite 对象。
 * @returns 原点 Sprite 对象。
 */
export function buildOriginPoint(
  frame: GlbDatumOriginFrame,
  color: number,
  sprite?: THREE.Sprite,
): THREE.Sprite {
  const target = sprite ?? createOriginPoint(color)
  updateOriginPoint(target, frame, color)
  return target
}

/**
 * 将 datum-slot 原点 frame 更新到 Sprite。
 *
 * @param sprite 原点 Sprite 对象。
 * @param frame 原点 frame。
 * @param color 点标记颜色。
 * @returns 原点 Sprite 对象。
 */
export function updateOriginPoint(
  sprite: THREE.Sprite,
  frame: GlbDatumOriginFrame,
  color: number,
): THREE.Sprite {
  const material = sprite.material
  if (material instanceof THREE.SpriteNodeMaterial) {
    material.color.setHex(color)
  }

  sprite.position.copy(frame.origin)
  sprite.scale.set(ORIGIN_SPRITE_WORLD_SCALE, ORIGIN_SPRITE_WORLD_SCALE, 1)
  sprite.visible = true
  return sprite
}
